import type { Plan } from '@customerio/hydra-ui-api';
import { formatNumber } from '@/utils/number';
import { BillingSystem } from '../types';
import type { BillingPlan, Highlight } from '../types';
import { FeatureGroup, featureGroups } from '../feature-groups';
import { Plans, PlanType, StartupType } from '../plans';

/**
 * Runtime context needed to transform a Stripe plan. In Ember these came from the
 * billing/features services via getters; because Hydra's billing data is static after
 * load, the billing service captures them once at load time and passes them in, keeping
 * this transform pure.
 */
export interface StripePlanContext {
  /** From the account's billing info (`billingInfo.cancellable`). */
  cancellable: boolean;
  /** Whether the account is on a trial — drives the feature-group selection. */
  isAccountTrial: boolean;
  /** The account's startup program tier, if any. */
  startupProgram?: StartupType;
  /** Whether the `new_pipelines_objects_pricing` feature flag is enabled. */
  newPipelinesObjectsPricing: boolean;
}

/**
 * A Stripe plan transformed into the shared {@link BillingPlan} shape, with the raw
 * wire fields preserved side-by-side (mirroring the Ember model instance).
 */
export type StripeBillingPlan = Plan & BillingPlan & { billingSystem: typeof BillingSystem.Stripe };

function intervalDisplayName(intervalName: string): string {
  switch (intervalName) {
    case 'month':
      return 'monthly';
    case 'quarter':
      return 'quarterly';
    case 'biannual':
      return 'semi-annual';
    case 'year':
      return 'annual';
    default:
      // Purposeful default
      return 'custom';
  }
}

function isTrialPlan(name: string): boolean {
  return ['Free Trial', 'Premium Trial'].includes(name);
}

// fallow-ignore-next-line complexity — ported from Ember billing, complexity is inherent to the domain mapping
function featureGroupsFor(plan: Plan, context: StripePlanContext): FeatureGroup[] {
  if (context.isAccountTrial) {
    return [FeatureGroup.FG8];
  }

  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const planType = plan.plan_type as PlanType;

  switch (planType) {
    case PlanType.Essentials:
    case PlanType.Builder:
    case PlanType.NotReportable:
      return [FeatureGroup.FG1];
    case PlanType.Premium:
      return [FeatureGroup.FG2];
    case PlanType.Enterprise:
      return [FeatureGroup.FG3];
    case PlanType.Startup:
      if (context.startupProgram === StartupType.Plus) {
        return [FeatureGroup.FG5];
      }
      return [FeatureGroup.FG4];
    case PlanType.CdpStarter:
    case PlanType.CdpPremium:
      return [FeatureGroup.FG6];
    case PlanType.Basic:
      return [FeatureGroup.FG7];
    default:
      // `plan.plan_type` is a wire string cast to `PlanType`, so an absent or unknown
      // value can reach here at runtime. Fall back to FG1 (matches the Ember service)
      // rather than an exhaustive `never` return, which would leak the raw value.
      return [FeatureGroup.FG1];
  }
}

// fallow-ignore-next-line complexity — ported from Ember billing, complexity is inherent to the domain mapping
function highlights(plan: Plan, groups: FeatureGroup[], context: StripePlanContext): Highlight[] {
  const planTemplate = Object.values(Plans).find((template) => template.slug === plan.slug);

  if (planTemplate != null) {
    if (context.newPipelinesObjectsPricing) {
      return planTemplate.highlightsNewPipelinesObjectsPricing ?? planTemplate.highlights;
    }
    return planTemplate.highlights;
  }

  const featureGroup = featureGroups[groups[0]] ?? {};
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const planType = plan.plan_type as PlanType;

  if (planType === PlanType.CdpPremium || planType === PlanType.CdpStarter) {
    const workspaceLimit = featureGroup.featureWorkspaces?.limit ?? 0;
    return [
      `<strong>${formatNumber(plan.cdp_api_calls)}</strong> monthly API calls`,
      `<strong>Unlimited</strong> sources`,
      `<strong>Unlimited</strong> destinations`,
      `<strong>${workspaceLimit === 0 ? 'Unlimited' : formatNumber(workspaceLimit)}</strong> workspaces`,
    ];
  }

  return [
    `<strong>Custom</strong> profile volume`,
    `<strong>Custom</strong> email volume`,
    `<strong>10</strong> object types`,
  ];
}

/** Transform a Stripe plan wire model into the shared {@link BillingPlan} shape. */
export function stripePlanToBillingPlan(plan: Plan, context: StripePlanContext): StripeBillingPlan {
  const groups = featureGroupsFor(plan, context);
  const planTemplate = Object.values(Plans).find((template) => template.slug === plan.slug);

  return {
    ...plan,
    billingSystem: BillingSystem.Stripe,
    basePrice: plan.annual ? plan.annual_price : plan.price,
    intervalDisplayName: intervalDisplayName(plan.interval_name),
    includedCustomers: plan.customers,
    includedMessages: plan.emails_sent,
    includedObjects: plan.objects,
    includedProfiles: plan.customers,
    customerOverageRate: plan.metered_customer_rate,
    // TODO(ordway/plans): message overage rate is not present on the Stripe plan.
    messageOverageRate: '',
    objectOverageRate: plan.metered_object_rate,
    profileOverageRate: plan.metered_customer_rate,
    canCancel: context.cancellable,
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    planType: plan.plan_type as PlanType,
    featureGroups: groups,
    name: plan.name,
    description: planTemplate?.description,
    cdpPremiumPrice: plan.cdp_premium_price,
    isPaidAnnually: plan.annual,
    annualPrice: plan.annual_price,
    highlights: highlights(plan, groups, context),
    isTrial: isTrialPlan(plan.name),
    isMauPricing: false,
  };
}
